import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import {
  applyMatterLitigationDatePlanV1,
  createMatterLitigationDatePlanV1,
  deleteMatterLitigationDatePlanV1,
  updateMatterLitigationDatePlanV1,
} from "@/lib/data/requests/matters/matter-litigation-date-plan";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMatterLitigationDatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMatterLitigationDatePlanV1>[0],
    ) => createMatterLitigationDatePlanV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              litigationDatePlans: [...oldData.data.litigationDatePlans, data],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter litigation date plan");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter litigation date plan created successfully");
    },
  });
};

export const useUpdateMatterLitigationDatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMatterLitigationDatePlanV1>[0],
    ) => updateMatterLitigationDatePlanV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              litigationDatePlans: oldData.data.litigationDatePlans.map(
                (item) => (item.id === data.id ? { ...item, ...data } : item),
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter litigation date plan");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter litigation date plan updated successfully");
    },
  });
};

export const useDeleteMatterLitigationDatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMatterLitigationDatePlanV1>[0],
    ) => deleteMatterLitigationDatePlanV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              litigationDatePlans: oldData.data.litigationDatePlans.filter(
                (item) => item.id !== data.id,
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting matter litigation date plan");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter litigation date plan deleted successfully");
    },
  });
};

export const useApplyMatterLitigationDatePlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof applyMatterLitigationDatePlanV1>[0]) =>
      applyMatterLitigationDatePlanV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error applying matter litigation date plan");
    },
    onSettled: () => {
      const invalidate = () => {
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.matters],
        });
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.matterLitigationDatePlanDates],
        });
      };

      setTimeout(invalidate, 2500);
      setTimeout(invalidate, 4800);
    },
    onSuccess: () => {
      toast.success("Matter litigation date plan applied successfully");
    },
  });
};
